import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { applyTheme, getStoredTheme, THEMES } from '../utils/themes';
import api from '../api';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [themeId, setThemeId] = useState(getStoredTheme());
  const [serverTheme, setServerTheme] = useState(null);
  const [currentGuildId, setCurrentGuildId] = useState(null);

  useEffect(() => {
    applyTheme(themeId);
  }, [themeId]);

  // Load theme for a specific guild
  const loadGuildTheme = useCallback(async (guildId) => {
    setCurrentGuildId(guildId);
    try {
      const res = await api.get(`/api/themes/${guildId}`);
      const { active_theme, server_theme } = res.data;
      setServerTheme(server_theme);
      setThemeId(active_theme);
      applyTheme(active_theme);
      localStorage.setItem('brazyy_theme', active_theme);
    } catch {
      // Fall back to stored theme
      const stored = getStoredTheme();
      setThemeId(stored);
      applyTheme(stored);
    }
  }, []);

  // Change personal theme (override for this guild only)
  const changeTheme = useCallback(async (id, guildId) => {
    if (!THEMES[id]) return;
    setThemeId(id);
    applyTheme(id);
    localStorage.setItem('brazyy_theme', id);

    // Save to backend if in a guild context
    const targetGuild = guildId || currentGuildId;
    if (targetGuild) {
      try {
        await api.patch(`/api/themes/${targetGuild}/personal`, { theme: id });
      } catch { /* silent */ }
    }
  }, [currentGuildId]);

  // Change server theme (affects all admins by default)
  const changeServerTheme = useCallback(async (id, guildId) => {
    if (!THEMES[id]) return;
    const targetGuild = guildId || currentGuildId;
    if (!targetGuild) return;
    try {
      await api.patch(`/api/themes/${targetGuild}/server`, { theme: id });
      setServerTheme(id);
    } catch { /* silent */ }
  }, [currentGuildId]);

  // Reset personal override
  const resetToServerTheme = useCallback(async (guildId) => {
    const targetGuild = guildId || currentGuildId;
    if (!targetGuild || !serverTheme) return;
    try {
      await api.delete(`/api/themes/${targetGuild}/personal`);
      setThemeId(serverTheme);
      applyTheme(serverTheme);
      localStorage.setItem('brazyy_theme', serverTheme);
    } catch { /* silent */ }
  }, [currentGuildId, serverTheme]);

  return (
    <ThemeContext.Provider value={{
      themeId, serverTheme, currentGuildId,
      changeTheme, changeServerTheme, resetToServerTheme,
      loadGuildTheme,
      theme: THEMES[themeId]
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
